import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cachedFetch, fetchCacheKey, invalidateFetchCache } from '../../utils/fetchCache';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import axios from 'axios';
import QRCode from 'qrcode';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Image,
  File,
  Download,
  Search,
  Filter,
  X,
  Eye,
  Trash2,
  Edit3,
  RotateCw,
  RotateCcw,
  Shield,
  Home,
  Mail,
  FileCheck,
  FolderOpen,
  AlertTriangle,
  Lock,
  Info,
  Plus,
  MoreVertical,
  ZoomIn,
  ZoomOut,
  MessageSquare,
  Calendar,
  User,
  Replace,
  Smartphone,
  QrCode,
  Check,
  Sparkles,
  SunMedium,
  Contrast,
  Focus,
  Send,
  Loader2,
  Phone,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// FundElite Premium Dark Emerald Design Colors (ONLY emerald/gold/red - NO purple)
const colors = {
  // Emerald family (primary accent)
  emerald: '#0fac6c',
  emeraldLight: '#22c55e',
  emeraldDark: '#0a653d',
  emeraldGlow: 'rgba(15,172,108,.18)',
  
  // Gold (secondary accent for review/pending)
  gold: '#d6b97a',
  goldBright: '#f59e0b',
  
  // Red (warning/error only)
  red: '#ef4444',
  
  // Dark surfaces
  bg: '#031810',
  bg2: '#052116',
  panel: '#072519',
  panel2: '#0a2d1f',
  panel3: '#0b3324',
  
  // Lines & borders
  line: 'rgba(154, 255, 205, 0.12)',
  lineStrong: 'rgba(154, 255, 205, 0.22)',
  
  // Text
  text: '#eaf7f0',
  muted: '#9cc9b2',
  soft: '#74b995',
  
  // Chip
  chip: '#103124',
};

// Document Categories Configuration (EMERALD/GOLD/RED only - NO purple)
const DOCUMENT_CATEGORIES = {
  identity_verification: {
    id: 'identity_verification',
    label: 'Identity Verification',
    icon: User,
    color: '#34d399', // emerald-400
    description: 'Government-issued photo ID documents',
    examples: ["Driver's License", 'State ID', 'Passport', 'Government Photo ID'],
    required: true,
    sensitive: true,
  },
  ssn_support: {
    id: 'ssn_support',
    label: 'SSN / Identity Support',
    icon: Shield,
    color: '#10b981', // emerald-500
    description: 'Social Security verification documents',
    examples: ['SSN Card', 'SSN Verification Document', 'Official SSN Letter'],
    required: false,
    sensitive: true,
  },
  proof_of_address: {
    id: 'proof_of_address',
    label: 'Proof of Address',
    icon: Home,
    color: '#0fac6c', // brand emerald
    description: 'Documents verifying your current address',
    examples: ['Bank Statement', 'Utility Bill', 'Lease Agreement', 'Mortgage Statement'],
    required: true,
    sensitive: false,
  },
  letters_received: {
    id: 'letters_received',
    label: 'Letters Received',
    icon: Mail,
    color: '#d6b97a', // gold
    description: 'Response letters from bureaus, creditors, or collectors',
    examples: ['Bureau Response Letter', 'Creditor Response', 'Collection Letter'],
    required: false,
    sensitive: false,
  },
  letters_sent: {
    id: 'letters_sent',
    label: 'Letters Sent',
    icon: FileCheck,
    color: '#22c55e', // emerald light
    description: "Dispute letters and correspondence you've sent",
    examples: ['Dispute Letter', 'Factual Dispute', 'AI Metro 2 Letter'],
    required: false,
    sensitive: false,
  },
  credit_reports: {
    id: 'credit_reports',
    label: 'Credit Reports & Case Docs',
    icon: FileText,
    color: '#059669', // emerald-600
    description: 'Credit reports and supporting case documents',
    examples: ['Credit Report', 'Supporting Report Pages', 'Dispute Evidence'],
    required: true,
    sensitive: true,
  },
  other: {
    id: 'other',
    label: 'Other Supporting Documents',
    icon: FolderOpen,
    color: '#9cc9b2', // muted emerald
    description: 'Any other relevant documents',
    examples: ['Supporting Evidence', 'Additional Documentation'],
    required: false,
    sensitive: false,
  },
};

// Document Status Configuration (Dark theme compatible)
const DOCUMENT_STATUS = {
  uploaded: { label: 'Uploaded', color: 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/30', icon: Upload },
  pending_review: { label: 'Pending Review', color: 'bg-amber-900/30 text-amber-300 border border-amber-700/30', icon: Clock },
  cropped_draft: { label: 'Cropped Draft', color: 'bg-amber-900/30 text-amber-300 border border-amber-700/30', icon: Focus },
  approved: { label: 'Approved', color: 'bg-emerald-900/40 text-emerald-300 border border-emerald-600/30', icon: CheckCircle },
  verified: { label: 'Verified', color: 'bg-emerald-900/50 text-emerald-200 border border-emerald-500/30', icon: Shield },
  replace_needed: { label: 'Replace Needed', color: 'bg-red-900/30 text-red-300 border border-red-700/30', icon: Replace },
};

const DOCUMENT_STATUS_I18N_KEYS = {
  uploaded: 'uploaded',
  pending_review: 'pendingReview',
  cropped_draft: 'croppedDraft',
  approved: 'approved',
  verified: 'verified',
  replace_needed: 'replaceNeeded',
};

const getDocumentStatusLabel = (statusKey, t) => {
  const key = DOCUMENT_STATUS_I18N_KEYS[statusKey];
  return key ? t(`documents.statusLabels.${key}`) : statusKey;
};

// Delivery Channels Configuration
const DELIVERY_CHANNELS = {
  mail: {
    id: 'mail',
    label: 'Mail',
    icon: Send,
    status: 'active',
    description: 'USPS Postal Mail via PostGrid',
  },
  fax: {
    id: 'fax',
    label: 'Fax',
    icon: Phone,
    status: 'coming_soon',
    description: 'Coming Soon',
  },
};

// ============================================
// STAT CARD COMPONENT - Light Mode Base with Dark Mode Override
// Premium Dashboard-matching emerald-dark system - UNIFIED DARK SURFACES
// ============================================
const StatCard = ({ icon: Icon, label, value, color, onClick }) => {
  // Determine icon background color based on the color prop
  const getIconBgClass = () => {
    if (color === '#10b981' || color === '#34d399') return 'bg-emerald-100 dark:bg-[rgba(52,211,153,0.06)] dark:border dark:border-[rgba(52,211,153,0.10)]';
    if (color === '#f59e0b' || color === '#d6b97a') return 'bg-amber-100 dark:bg-[rgba(251,191,36,0.06)] dark:border dark:border-[rgba(251,191,36,0.10)]';
    if (color === '#ef4444') return 'bg-red-100 dark:bg-[rgba(239,68,68,0.06)] dark:border dark:border-[rgba(239,68,68,0.10)]';
    return 'bg-stone-100 dark:bg-[rgba(255,255,255,0.03)] dark:border dark:border-[rgba(255,255,255,0.05)]';
  };

  return (
    <button
      onClick={onClick}
      className="premium-stat-card relative overflow-hidden min-h-[98px] rounded-[20px] p-[16px_18px] text-left w-full transition-all duration-200 hover:shadow-lg group bg-white border border-stone-200 dark:bg-[linear-gradient(180deg,rgba(6,32,22,0.96)_0%,rgba(4,22,15,0.98)_100%)] dark:border-[rgba(52,211,153,0.10)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.35)] dark:hover:border-[rgba(52,211,153,0.16)]"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Dark mode premium panel glow overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[radial-gradient(circle_at_8%_12%,rgba(52,211,153,0.05),transparent_26%)]" />
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[linear-gradient(180deg,rgba(52,211,153,0.025)_0%,transparent_50%)]" />
      
      <div className="relative z-10 flex items-center gap-3 mb-[14px]">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${getIconBgClass()}`}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      <p className="relative z-10 text-[17px] font-extrabold leading-none text-stone-900 dark:text-[#e8fef1] mb-2">
        {value}
      </p>
      <p className="relative z-10 text-[13px] text-stone-500 dark:text-[#7fb89a]">
        {label}
      </p>
    </button>
  );
};

// ============================================
// QR SCAN MODAL
// ============================================
const QRScanModal = ({ isOpen, onClose, onSessionCreated, t }) => {
  const { token } = useAuth();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('creating');
  const [expiresIn, setExpiresIn] = useState(900);
  const pollingRef = useRef(null);

  // Create scan session
  useEffect(() => {
    if (!isOpen) return;

    const createSession = async () => {
      try {
        setSessionStatus('creating');
        const response = await axios.post(
          `${API}/documents/scan-sessions/create`,
          { target_category: null },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { session_id, qr_data, expires_in_seconds } = response.data;
        setSessionId(session_id);
        setExpiresIn(expires_in_seconds);

        // Generate QR code
        const qrUrl = await QRCode.toDataURL(qr_data, {
          width: 256,
          margin: 2,
          color: { dark: '#111827', light: '#FFFFFF' },
        });
        setQrCodeUrl(qrUrl);
        setSessionStatus('ready');

        // Start polling
        startPolling(session_id);
      } catch (err) {
        console.error('Failed to create scan session:', err);
        toast.error(t('documents.qrScan.errorCreating'));
        setSessionStatus('error');
      }
    };

    createSession();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isOpen, token, t]);

  // Poll for session status
  const startPolling = (sid) => {
    pollingRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/documents/scan-sessions/${sid}/status`);
        const { status, document_id } = response.data;

        if (status === 'completed' && document_id) {
          clearInterval(pollingRef.current);
          setSessionStatus('completed');
          toast.success(t('documents.qrScan.documentReceived'));
          onSessionCreated?.(document_id);
          setTimeout(onClose, 2000);
        } else if (status === 'expired') {
          clearInterval(pollingRef.current);
          setSessionStatus('expired');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);
  };

  // Countdown timer
  useEffect(() => {
    if (sessionStatus !== 'ready') return;

    const timer = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionStatus]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="qr-scan-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone size={20} style={{ color: colors.brandGreen500 }} />
            {t('documents.qrScan.title')}
          </DialogTitle>
          <DialogDescription>
            {t('documents.qrScan.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {sessionStatus === 'creating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2
                size={48}
                className="animate-spin mb-4"
                style={{ color: colors.brandGreen500 }}
              />
              <p style={{ color: colors.textSecondary }}>{t('documents.qrScan.creatingSession')}</p>
            </div>
          )}

          {sessionStatus === 'ready' && qrCodeUrl && (
            <div className="flex flex-col items-center">
              {/* QR Code */}
              <div
                className="p-4 rounded-2xl mb-4"
                style={{ background: colors.mint50, border: `2px solid ${colors.mint100}` }}
              >
                <img src={qrCodeUrl} alt={t('documents.qrScan.qrAlt')} className="w-56 h-56" />
              </div>

              {/* Timer */}
              <div className="flex items-center gap-2 mb-4">
                <Clock size={16} style={{ color: expiresIn < 120 ? colors.warning : colors.textMuted }} />
                <span 
                  className="text-sm font-medium" 
                  style={{ color: expiresIn < 120 ? colors.warning : colors.textMuted }}
                >
                  {t('documents.qrScan.expiresIn')} {formatTime(expiresIn)}
                </span>
              </div>

              {/* Improved Instructions */}
              <div
                className="w-full p-4 rounded-xl"
                style={{ background: colors.mint50, border: `1px solid ${colors.mint100}` }}
              >
                <h4 className="font-medium text-sm mb-3" style={{ color: colors.textPrimary }}>
                  {t('documents.qrScan.howToScanTitle')}
                </h4>
                <ol className="space-y-2 text-sm" style={{ color: colors.textSecondary }}>
                  <li className="flex items-start gap-2">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ background: colors.brandGreen500, color: '#FFF' }}
                    >
                      1
                    </span>
                    <span>{t('documents.qrScan.scanStep1')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ background: colors.brandGreen500, color: '#FFF' }}
                    >
                      2
                    </span>
                    <span>{t('documents.qrScan.scanStep2')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ background: colors.brandGreen500, color: '#FFF' }}
                    >
                      3
                    </span>
                    <span>{t('documents.qrScan.scanStep3')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ background: colors.brandGreen500, color: '#FFF' }}
                    >
                      4
                    </span>
                    <span>{t('documents.qrScan.scanStep4')}</span>
                  </li>
                </ol>
                
                {/* Tips */}
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-xs" style={{ color: colors.textMuted }}>
                    <strong>{t('documents.qrScan.scanTipsLabel')}</strong>{' '}
                    {t('documents.qrScan.scanTipsBody')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {sessionStatus === 'completed' && (
            <div className="flex flex-col items-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ background: `${colors.brandGreen500}15` }}
              >
                <CheckCircle size={32} style={{ color: colors.brandGreen500 }} />
              </div>
              <h3 className="font-semibold text-lg" style={{ color: colors.textPrimary }}>
                {t('documents.qrScan.documentReceived')}
              </h3>
              <p className="text-sm text-center mt-2" style={{ color: colors.textSecondary }}>
                {t('documents.qrScan.documentUploadedSuccess')}
              </p>
            </div>
          )}

          {sessionStatus === 'expired' && (
            <div className="flex flex-col items-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ background: `${colors.warning}15` }}
              >
                <AlertTriangle size={32} style={{ color: colors.warning }} />
              </div>
              <h3 className="font-semibold text-lg" style={{ color: colors.textPrimary }}>
                {t('documents.qrScan.sessionExpired')}
              </h3>
              <p className="text-sm text-center mt-2 mb-4" style={{ color: colors.textSecondary }}>
                {t('documents.qrScan.sessionExpiredDesc')}
              </p>
              <Button
                onClick={() => {
                  setSessionStatus('creating');
                  setQrCodeUrl(null);
                  setSessionId(null);
                  // Re-trigger session creation
                  const createSession = async () => {
                    try {
                      const response = await axios.post(
                        `${API}/documents/scan-sessions/create`,
                        { target_category: null },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      const { session_id, qr_data, expires_in_seconds } = response.data;
                      setSessionId(session_id);
                      setExpiresIn(expires_in_seconds);
                      const qrUrl = await QRCode.toDataURL(qr_data, {
                        width: 256,
                        margin: 2,
                        color: { dark: '#111827', light: '#FFFFFF' },
                      });
                      setQrCodeUrl(qrUrl);
                      setSessionStatus('ready');
                      startPolling(session_id);
                    } catch (err) {
                      console.error('Failed to create scan session:', err);
                      toast.error(t('documents.qrScan.errorCreating'));
                      setSessionStatus('error');
                    }
                  };
                  createSession();
                }}
                style={{ background: colors.brandGreen500, color: '#FFF' }}
              >
                <RotateCcw size={16} className="mr-2" />
                {t('documents.qrScan.generateNewQr')}
              </Button>
            </div>
          )}

          {sessionStatus === 'error' && (
            <div className="flex flex-col items-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ background: `${colors.error}15` }}
              >
                <AlertCircle size={32} style={{ color: colors.error }} />
              </div>
              <h3 className="font-semibold text-lg" style={{ color: colors.textPrimary }}>
                {t('common.error')}
              </h3>
              <p className="text-sm text-center mt-2" style={{ color: colors.textSecondary }}>
                {t('documents.qrScan.errorCreating')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// IMAGE EDITOR MODAL
// ============================================
const ImageEditorModal = ({ isOpen, onClose, document, onSave }) => {
  const { t } = useI18n();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Crop settings
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [sharpness, setSharpness] = useState(1);
  const [activeVersion, setActiveVersion] = useState('original');

  // Initialize from document
  useEffect(() => {
    if (document?.current_crop_settings) {
      const settings = document.current_crop_settings;
      setRotation(settings.rotation || 0);
      setBrightness(settings.brightness || 1);
      setContrast(settings.contrast || 1);
      setSharpness(settings.sharpness || 1);
    } else {
      setRotation(0);
      setBrightness(1);
      setContrast(1);
      setSharpness(1);
    }
    setActiveVersion(document?.active_version || 'original');
  }, [document]);

  const getCurrentFileUrl = () => {
    if (!document) return null;

    const versions = {
      original: document.original_file,
      ai_cropped: document.ai_cropped_file,
      manual_adjusted: document.manual_adjusted_file,
      approved: document.approved_file,
    };

    const version = versions[activeVersion];
    if (version?.file_url) {
      return version.file_url.startsWith('/api')
        ? `${BACKEND_URL}${version.file_url}`
        : version.file_url;
    }
    return null;
  };

  const handleRunAIDetection = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/documents/detect-edges/${document.document_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.crop_settings) {
        const settings = response.data.crop_settings;
        setRotation(settings.rotation || 0);
        setBrightness(settings.brightness || 1);
        setContrast(settings.contrast || 1);
        setSharpness(settings.sharpness || 1);
        toast.success(t('documents.toast.aiDetectionComplete'));
      }
    } catch (err) {
      toast.error(t('documents.toast.aiDetectionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCrop = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/documents/apply-crop`,
        {
          document_id: document.document_id,
          crop_settings: {
            x: 0,
            y: 0,
            width: 100,
            height: 100,
            rotation,
            brightness,
            contrast,
            sharpness,
          },
          save_as: 'manual_adjusted',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t('documents.toast.adjustmentsApplied'));
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(t('documents.toast.adjustmentsFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRotation(0);
    setBrightness(1);
    setContrast(1);
    setSharpness(1);
    setActiveVersion('original');
  };

  const imageUrl = getCurrentFileUrl();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="image-editor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 size={20} style={{ color: colors.brandGreen500 }} />
            {t('documents.editor.title', { title: document?.title })}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-4">
          {/* Preview */}
          <div className="lg:col-span-2">
            <div
              className="aspect-[4/3] rounded-xl overflow-hidden flex items-center justify-center"
              style={{ background: '#F1F5F9' }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={document?.title}
                  className="max-w-full max-h-full object-contain transition-all duration-200"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    filter: `brightness(${brightness}) contrast(${contrast})`,
                  }}
                />
              ) : (
                <div className="text-center">
                  <FileText size={48} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textMuted }}>{t('documents.editor.noPreview')}</p>
                </div>
              )}
            </div>

            {/* Version selector */}
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm" style={{ color: colors.textMuted }}>
                {t('documents.editor.version')}
              </span>
              {['original', 'ai_cropped', 'manual_adjusted'].map((ver) => {
                const hasVersion =
                  ver === 'original' || document?.[`${ver}_file`];
                if (!hasVersion) return null;
                return (
                  <button
                    key={ver}
                    onClick={() => setActiveVersion(ver)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      activeVersion === ver ? 'font-medium' : ''
                    }`}
                    style={{
                      background:
                        activeVersion === ver ? colors.mint100 : 'transparent',
                      color:
                        activeVersion === ver
                          ? colors.brandGreen600
                          : colors.textSecondary,
                    }}
                  >
                    {ver === 'original'
                      ? t('documents.editor.versionOriginal')
                      : ver === 'ai_cropped'
                      ? t('documents.editor.versionAiCrop')
                      : t('documents.editor.versionManual')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-6">
            {/* AI Detection */}
            <div>
              <Button
                onClick={handleRunAIDetection}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                {loading ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Sparkles size={16} className="mr-2" style={{ color: '#F59E0B' }} />
                )}
                {t('documents.editor.aiSuggestCrop')}
              </Button>
              {document?.current_crop_settings?.ai_confidence > 0 && (
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  {t('documents.editor.aiConfidence', {
                    percent: Math.round(document.current_crop_settings.ai_confidence * 100),
                  })}
                </p>
              )}
            </div>

            {/* Rotation */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <RotateCw size={14} />
                {t('documents.editor.rotation')}
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                >
                  <RotateCcw size={14} />
                </Button>
                <span
                  className="flex-1 text-center text-sm font-medium"
                  style={{ color: colors.textPrimary }}
                >
                  {rotation}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                >
                  <RotateCw size={14} />
                </Button>
              </div>
            </div>

            {/* Brightness */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <SunMedium size={14} />
                {t('documents.editor.brightness', { value: brightness.toFixed(1) })}
              </Label>
              <Slider
                value={[brightness]}
                onValueChange={([v]) => setBrightness(v)}
                min={0.5}
                max={2}
                step={0.1}
              />
            </div>

            {/* Contrast */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Contrast size={14} />
                {t('documents.editor.contrast', { value: contrast.toFixed(1) })}
              </Label>
              <Slider
                value={[contrast]}
                onValueChange={([v]) => setContrast(v)}
                min={0.5}
                max={2}
                step={0.1}
              />
            </div>

            {/* Sharpness */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Focus size={14} />
                {t('documents.editor.sharpness', { value: sharpness.toFixed(1) })}
              </Label>
              <Slider
                value={[sharpness]}
                onValueChange={([v]) => setSharpness(v)}
                min={0.5}
                max={2}
                step={0.1}
              />
            </div>

            {/* Reset */}
            <Button variant="ghost" onClick={handleReset} className="w-full">
              <RefreshCw size={14} className="mr-2" />
              {t('documents.editor.resetAll')}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleApplyCrop}
            disabled={saving}
            className="text-white"
            style={{ background: colors.brandGreen500 }}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                {t('documents.editor.saving')}
              </>
            ) : (
              <>
                <Check size={16} className="mr-2" />
                {t('documents.editor.applyAdjustments')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// DOCUMENT CARD - Light Mode Base with Dark Mode Override
// Premium Dashboard-matching emerald-dark system
// ============================================
const DocumentCard = ({
  document,
  onView,
  onEdit,
  onDelete,
  onApprove,
  onRevokeApproval,
}) => {
  const { t } = useI18n();
  const category = DOCUMENT_CATEGORIES[document.category] || DOCUMENT_CATEGORIES.other;
  const status = DOCUMENT_STATUS[document.status] || DOCUMENT_STATUS.uploaded;
  const categoryLabel =
    t(`documents.categoryLabels.${document.category}`) || category.label;
  const StatusIcon = status.icon;
  const isSensitive = category.sensitive;

  // Get display file URL
  const getFileUrl = () => {
    const versions = {
      original: document.original_file,
      ai_cropped: document.ai_cropped_file,
      manual_adjusted: document.manual_adjusted_file,
      approved: document.approved_file,
    };
    const version = versions[document.active_version] || document.original_file;
    if (version?.file_url) {
      return version.file_url.startsWith('/api')
        ? `${BACKEND_URL}${version.file_url}`
        : version.file_url;
    }
    return null;
  };

  const isImage = document.original_file?.mime_type?.startsWith('image/');
  const fileUrl = getFileUrl();

  return (
    <div
      className="relative overflow-hidden p-4 rounded-[20px] transition-all duration-200 hover:shadow-lg group bg-white border border-stone-200 dark:bg-[linear-gradient(180deg,rgba(7,39,28,0.96)_0%,rgba(5,25,18,0.98)_100%)] dark:border-emerald-500/[0.12] dark:shadow-[0_16px_40px_rgba(0,0,0,0.28)]"
      data-testid={`document-card-${document.document_id}`}
    >
      {/* Dark mode premium panel glow overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[radial-gradient(circle_at_12%_18%,rgba(56,189,106,0.10),transparent_32%)]" />
      
      <div className="relative z-10 flex items-start gap-4">
        {/* Thumbnail */}
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 relative overflow-hidden cursor-pointer transition-all border border-stone-200 dark:border-white/[0.05]"
          style={{ background: `${category.color}15` }}
          onClick={() => onView(document)}
        >
          {isImage && fileUrl ? (
            <img
              src={fileUrl}
              alt={document.title}
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <category.icon size={28} style={{ color: category.color }} />
          )}
          {isSensitive && (
            <div
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center bg-emerald-600"
              title={t('documents.sensitiveDocument')}
            >
              <Lock size={10} className="text-white" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4
                className="font-semibold text-sm truncate cursor-pointer transition-colors text-stone-900 hover:text-emerald-600 dark:text-[#ecfff3] dark:hover:text-[#6ef3aa]"
                onClick={() => onView(document)}
              >
                {document.title}
              </h4>
              <p className="text-xs mt-0.5 text-stone-500 dark:text-[#78c49b]">
                {categoryLabel}
              </p>
            </div>

            {/* Status Badge */}
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}
            >
              <StatusIcon size={12} />
              {getDocumentStatusLabel(document.status, t)}
            </span>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-3 mt-2.5 text-xs text-stone-500 dark:text-[#78c49b]">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(document.uploaded_at).toLocaleDateString()}
            </span>
            {document.capture_source === 'mobile_scan' && (
              <span className="flex items-center gap-1">
                <Smartphone size={12} />
                {t('documents.mobileCapture')}
              </span>
            )}
            {document.is_attachment_ready && (
              <span className="flex items-center gap-1 font-medium text-emerald-600 dark:text-[#6ef3aa]">
                <FileCheck size={12} />
                {t('common.attachmentReady')}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(document)}>
              <Eye size={14} className="mr-2" />
              {t('common.view')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(document)}>
              <Edit3 size={14} className="mr-2" />
              {t('documents.adjustCrop')}
            </DropdownMenuItem>
            {fileUrl && (
              <DropdownMenuItem onClick={() => window.open(fileUrl, '_blank')}>
                <Download size={14} className="mr-2" />
                {t('common.download')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {document.status !== 'approved' && document.status !== 'verified' ? (
              <DropdownMenuItem onClick={() => onApprove(document)}>
                <CheckCircle size={14} className="mr-2 text-emerald-500" />
                {t('documents.approveForAttachment')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onRevokeApproval(document)}>
                <X size={14} className="mr-2" />
                {t('documents.revokeApproval')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(document)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 size={14} className="mr-2" />
              {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// ============================================
// UPLOAD MODAL
// ============================================
const UploadModal = ({ isOpen, onClose, onUpload, preselectedCategory, t }) => {
  const [selectedCategory, setSelectedCategory] = useState(preselectedCategory || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    setError('');

    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      setError(t('documents.upload.unsupportedFileType'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(t('documents.upload.fileTooLarge'));
      return;
    }

    setSelectedFile(file);
    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !selectedCategory || !title) {
      setError(t('documents.upload.fillRequiredFields'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      formData.append('title', title);
      formData.append('description', description);

      await onUpload(formData);
      onClose();

      // Reset form
      setSelectedFile(null);
      setTitle('');
      setDescription('');
      setSelectedCategory('');
    } catch (err) {
      setError(err.message || t('documents.upload.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const category = DOCUMENT_CATEGORIES[selectedCategory];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={20} style={{ color: colors.brandGreen500 }} />
            {t('documents.upload.title')}
          </DialogTitle>
          <DialogDescription>
            {t('documents.upload.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label>{t('documents.upload.documentType')} *</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t('documents.upload.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DOCUMENT_CATEGORIES).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <cat.icon size={16} style={{ color: cat.color }} />
                      {t(`documents.categoryLabels.${key}`) || cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {category && selectedCategory && (
              <p className="text-xs" style={{ color: colors.textMuted }}>
                {t(`documents.categoryDescriptions.${selectedCategory}`) || category.description}
              </p>
            )}
          </div>

          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
              dragActive ? 'border-emerald-500 bg-emerald-50' : 'hover:border-gray-400'
            }`}
            style={{ borderColor: dragActive ? colors.brandGreen500 : colors.borderDefault }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => window.document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} style={{ color: colors.brandGreen500 }} />
                <div className="text-left">
                  <p className="font-medium text-sm" style={{ color: colors.textPrimary }}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {t('documents.upload.dragDropFiles')}
                </p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  {t('documents.upload.supportedFormatsHint')}
                </p>
              </>
            )}
          </div>

          {/* Document Title */}
          <div className="space-y-2">
            <Label>{t('documents.upload.titleLabel')} *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('documents.upload.titlePlaceholder')}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t('documents.upload.descriptionLabel')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('documents.upload.descriptionPlaceholder')}
              rows={2}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Security Notice */}
          {category?.sensitive && (
            <div
              className="p-3 rounded-lg flex items-start gap-2"
              style={{ background: colors.mint50 }}
            >
              <Lock
                size={16}
                className="flex-shrink-0 mt-0.5"
                style={{ color: colors.brandGreen600 }}
              />
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                {t('common.sensitiveDataSecure')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={uploading || !selectedFile || !selectedCategory || !title}
            className="text-white"
            style={{ background: colors.brandGreen500 }}
          >
            {uploading ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                {t('documents.upload.uploading')}
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                {t('documents.uploadDocument')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// PREVIEW MODAL
// ============================================
const PreviewModal = ({ document, isOpen, onClose }) => {
  const { t } = useI18n();
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  if (!document) return null;

  const getFileUrl = () => {
    const versions = {
      original: document.original_file,
      ai_cropped: document.ai_cropped_file,
      manual_adjusted: document.manual_adjusted_file,
      approved: document.approved_file,
    };
    const version = versions[document.active_version] || document.original_file;
    if (version?.file_url) {
      return version.file_url.startsWith('/api')
        ? `${BACKEND_URL}${version.file_url}`
        : version.file_url;
    }
    return null;
  };

  const isImage = document.original_file?.mime_type?.startsWith('image/');
  const isPdf = document.original_file?.mime_type === 'application/pdf';
  const fileUrl = getFileUrl();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{document.title}</span>
            {isImage && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setRotation((r) => (r + 90) % 360)}>
                  <RotateCw size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(z + 0.25, 2))}>
                  <ZoomIn size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}>
                  <ZoomOut size={14} />
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div
          className="mt-4 rounded-xl overflow-hidden flex items-center justify-center min-h-[400px]"
          style={{ background: '#F1F5F9' }}
        >
          {isImage && fileUrl ? (
            <img
              src={fileUrl}
              alt={document.title}
              className="max-w-full max-h-[60vh] object-contain transition-transform"
              style={{ transform: `rotate(${rotation}deg) scale(${zoom})` }}
            />
          ) : isPdf && fileUrl ? (
            <iframe src={fileUrl} title={document.title} className="w-full h-[60vh]" />
          ) : (
            <div className="text-center p-8">
              <FileText size={48} className="mx-auto mb-4" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textSecondary }}>{t('documents.preview.notAvailable')}</p>
              {fileUrl && (
                <Button onClick={() => window.open(fileUrl, '_blank')} className="mt-4">
                  <Download size={16} className="mr-2" />
                  {t('documents.preview.downloadToView')}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// DELIVERY CHANNELS SECTION - Light Mode Base with Dark Mode Override
// Premium Dashboard-matching emerald-dark system - UNIFIED DARK SURFACES
// ============================================
const DeliveryChannelsSection = ({ t }) => {
  return (
    <div className="relative overflow-hidden rounded-[20px] p-[18px] mb-4 bg-white border border-stone-200 dark:bg-[linear-gradient(180deg,rgba(6,32,22,0.98)_0%,rgba(4,22,15,0.99)_100%)] dark:border-[rgba(52,211,153,0.10)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
      {/* Dark mode premium panel glow overlays */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[radial-gradient(circle_at_8%_12%,rgba(52,211,153,0.06),transparent_28%)]" />
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[linear-gradient(180deg,rgba(52,211,153,0.03)_0%,transparent_40%)]" />
      
      <div className="relative z-10">
        <h3 className="font-extrabold text-[18px] mb-1 tracking-[-0.02em] text-stone-900 dark:text-[#e8fef1]">
          {t('documents.deliveryChannels')}
        </h3>
        <p className="text-[14px] leading-[1.55] mb-4 text-stone-500 dark:text-[#7fb89a]">
          {t('documents.deliveryChannelsDesc')}
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Mail - Active - Deep emerald dark card */}
          <div className="flex items-center justify-between gap-3.5 min-h-[78px] p-[16px_18px] rounded-[16px] transition-all bg-emerald-50 border-2 border-emerald-400 dark:bg-[linear-gradient(180deg,rgba(8,38,26,0.95)_0%,rgba(5,28,19,0.98)_100%)] dark:border-[rgba(52,211,153,0.18)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.02),0_4px_16px_rgba(0,0,0,0.25)]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-[12px] flex items-center justify-center bg-emerald-100 border border-emerald-300 dark:bg-[rgba(52,211,153,0.08)] dark:border-[rgba(52,211,153,0.12)]">
                <Send size={18} className="text-emerald-600 dark:text-[#5ee9a0]" />
              </div>
              <div>
                <p className="font-bold text-[17px] leading-[1.2] text-stone-900 dark:text-[#e8fef1]">
                  {t('documents.mailDelivery')}
                </p>
                <p className="text-[14px] mt-1 text-emerald-600 dark:text-[#5ee9a0]">
                  {t('common.active')}
                </p>
              </div>
            </div>
            <ChevronRight size={18} className="text-emerald-500 dark:text-[#5ee9a0]" />
          </div>

          {/* Fax - Coming Soon - Deep neutral dark card */}
          <div className="flex items-center gap-3.5 min-h-[78px] p-[16px_18px] rounded-[16px] opacity-[0.50] bg-stone-50 border border-stone-200 dark:bg-[linear-gradient(180deg,rgba(6,28,20,0.90)_0%,rgba(4,20,14,0.95)_100%)] dark:border-[rgba(52,211,153,0.06)] dark:opacity-[0.45]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-[12px] flex items-center justify-center bg-stone-100 border border-stone-200 dark:bg-[rgba(255,255,255,0.025)] dark:border-[rgba(255,255,255,0.04)]">
                <Phone size={18} className="text-stone-400 dark:text-[#9fc9b2]" />
              </div>
              <div>
                <p className="font-bold text-[17px] leading-[1.2] text-stone-500 dark:text-[#c8e8d6]">
                  {t('documents.faxDelivery')}
                </p>
                <p className="text-[14px] mt-1 text-stone-400 dark:text-[#7fb89a]">
                  {t('common.comingSoon')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const MyDocumentsPage = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  // Modals
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [qrScanModalOpen, setQrScanModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [editDocument, setEditDocument] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('');

  // Fetch documents
  const fetchDocuments = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    try {
      const cacheKey = fetchCacheKey(
        'documents-list',
        token,
        activeCategory,
        statusFilter,
        searchQuery,
        sortOrder
      );
      if (force) {
        invalidateFetchCache(cacheKey);
      }
      const data = await cachedFetch(
        cacheKey,
        async () => {
          const response = await axios.get(`${API}/documents/list`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              category: activeCategory !== 'all' ? activeCategory : undefined,
              status: statusFilter !== 'all' ? statusFilter : undefined,
              search: searchQuery || undefined,
              sort_order: sortOrder === 'newest' ? 'desc' : 'asc',
            },
          });
          return response.data;
        },
        { ttlMs: 15000, force }
      );
      setDocuments(data.items || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [token, activeCategory, statusFilter, searchQuery, sortOrder]);

  useEffect(() => {
    if (token) fetchDocuments();
  }, [token, fetchDocuments]);

  // Upload handler
  const handleUpload = async (formData) => {
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(t('documents.toast.uploadSuccess'));
      fetchDocuments({ force: true });
    } catch (err) {
      console.error('Upload failed:', err);
      throw new Error(err.response?.data?.detail || t('documents.toast.uploadFailed'));
    }
  };

  // Delete handler
  const handleDelete = async (doc) => {
    if (!window.confirm(t('documents.deleteConfirm', { title: doc.title }))) return;

    try {
      await axios.delete(`${API}/documents/${doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('documents.toast.deleteSuccess'));
      fetchDocuments({ force: true });
    } catch (err) {
      toast.error(t('documents.toast.deleteFailed'));
    }
  };

  // Approve handler
  const handleApprove = async (doc) => {
    try {
      await axios.post(
        `${API}/documents/approve`,
        {
          document_id: doc.document_id,
          use_version: doc.manual_adjusted_file
            ? 'manual_adjusted'
            : doc.ai_cropped_file
            ? 'ai_cropped'
            : 'original',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('documents.toast.approveSuccess'));
      fetchDocuments({ force: true });
    } catch (err) {
      toast.error(t('documents.toast.approveFailed'));
    }
  };

  // Revoke approval handler
  const handleRevokeApproval = async (doc) => {
    try {
      await axios.post(`${API}/documents/revoke-approval/${doc.document_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('documents.toast.revokeSuccess'));
      fetchDocuments({ force: true });
    } catch (err) {
      toast.error(t('documents.toast.revokeFailed'));
    }
  };

  // Computed stats
  const stats = useMemo(() => {
    const total = documents.length;
    const pending = documents.filter(
      (d) => d.status === 'pending_review' || d.status === 'cropped_draft'
    ).length;
    const attachmentReady = documents.filter((d) => d.is_attachment_ready).length;
    const needsAction = documents.filter((d) => d.status === 'replace_needed').length;

    return { total, pending, attachmentReady, needsAction };
  }, [documents]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: documents.length };
    Object.keys(DOCUMENT_CATEGORIES).forEach((cat) => {
      counts[cat] = documents.filter((d) => d.category === cat).length;
    });
    return counts;
  }, [documents]);

  // Filtered documents
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];

    if (activeCategory !== 'all') {
      filtered = filtered.filter((d) => d.category === activeCategory);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((d) => d.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.title?.toLowerCase().includes(query) ||
          d.category?.toLowerCase().includes(query)
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.uploaded_at);
      const dateB = new Date(b.uploaded_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [documents, activeCategory, statusFilter, searchQuery, sortOrder]);

  const openUploadModal = (category = '') => {
    setUploadCategory(category);
    setUploadModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Loader2
            size={40}
            className="animate-spin"
            style={{ color: '#0fac6c' }}
          />
          <p className="text-sm" style={{ color: '#9cc9b2' }}>
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 min-h-[calc(100vh-80px)] dark:bg-[#041811] dark:bg-[radial-gradient(circle_at_35%_0%,rgba(45,190,115,0.08),transparent_24%),radial-gradient(circle_at_75%_8%,rgba(61,167,108,0.06),transparent_16%),linear-gradient(180deg,#041811_0%,#03140e_100%)] dark:-m-4 dark:lg:-m-6 dark:p-4 dark:lg:p-6 dark:pb-12" data-testid="my-documents-page">
      {/* Header - Light Mode Base with Dark Mode Override */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold tracking-[-0.02em] text-stone-900 dark:text-[#ecfff3]">
            {t('documents.myDocuments')}
          </h1>
          <p className="text-[13px] mt-2 text-stone-500 dark:text-[#9fd0b4]">
            {t('documents.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            disabled
            variant="outline"
            className="h-[42px] gap-2 rounded-[14px] px-4 font-bold border-stone-300 text-stone-400 cursor-not-allowed opacity-60 dark:border-[rgba(52,211,153,0.08)] dark:text-[#7a9f8a]"
            data-testid="scan-with-phone-btn"
          >
            <QrCode size={16} />
            {t('documents.scanWithPhone')}
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-semibold">{t('common.comingSoon')}</span>
          </Button>
          <Button
            onClick={() => openUploadModal()}
            className="h-[42px] gap-2 rounded-[14px] px-4 font-bold bg-gradient-to-b from-[#20c669] to-[#12aa5b] text-white border-0 shadow-[0_10px_20px_rgba(18,170,91,0.22)]"
            data-testid="upload-document-btn"
          >
            <Upload size={16} />
            {t('documents.uploadDocument')}
          </Button>
          <Button 
            onClick={fetchDocuments}
            variant="outline"
            className="h-[42px] w-[42px] p-0 rounded-[14px] border-stone-300 text-stone-700 dark:border-[rgba(52,211,153,0.10)] dark:text-[#7fb89a] dark:bg-[rgba(52,211,153,0.03)] dark:hover:bg-[rgba(52,211,153,0.06)]"
          >
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={FolderOpen}
          label={t('documents.totalDocuments')}
          value={stats.total}
          color="#10b981"
          onClick={() => setActiveCategory('all')}
        />
        <StatCard
          icon={Clock}
          label={t('common.pendingReview')}
          value={stats.pending}
          color="#f59e0b"
          onClick={() => setStatusFilter('pending_review')}
        />
        <StatCard
          icon={FileCheck}
          label={t('common.attachmentReady')}
          value={stats.attachmentReady}
          color="#10b981"
          onClick={() => setStatusFilter('approved')}
        />
        <StatCard
          icon={AlertTriangle}
          label={t('common.needsAction')}
          value={stats.needsAction}
          color="#ef4444"
          onClick={() => setStatusFilter('replace_needed')}
        />
      </div>

      {/* NOTE: Delivery Channels section removed - moved to Letter Preview / My Disputes sending flow */}

      {/* Category Filters - Light Mode Base with Dark Mode Override - UNIFIED DARK CHIPS */}
      <div className="flex flex-wrap gap-2.5 mb-4">
        <button
          onClick={() => setActiveCategory('all')}
          className={`inline-flex items-center gap-2 h-[36px] px-[14px] rounded-full text-[14px] font-medium transition-all border ${
            activeCategory === 'all'
              ? 'bg-emerald-100 border-emerald-400 text-emerald-700 dark:bg-[rgba(52,211,153,0.12)] dark:border-[rgba(52,211,153,0.22)] dark:text-[#7eedb8]'
              : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50 dark:bg-[rgba(6,28,20,0.70)] dark:border-[rgba(52,211,153,0.08)] dark:text-[#b8dcc8] dark:hover:bg-[rgba(52,211,153,0.06)] dark:hover:border-[rgba(52,211,153,0.12)]'
          }`}
        >
          <FolderOpen size={14} />
          {t('common.all')} ({categoryCounts.all})
        </button>
        {Object.entries(DOCUMENT_CATEGORIES).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`inline-flex items-center gap-2 h-[36px] px-[14px] rounded-full text-[14px] font-medium transition-all border ${
              activeCategory === key
                ? 'bg-emerald-100 border-emerald-400 text-emerald-700 dark:bg-[rgba(52,211,153,0.12)] dark:border-[rgba(52,211,153,0.22)] dark:text-[#7eedb8]'
                : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50 dark:bg-[rgba(6,28,20,0.70)] dark:border-[rgba(52,211,153,0.08)] dark:text-[#b8dcc8] dark:hover:bg-[rgba(52,211,153,0.06)] dark:hover:border-[rgba(52,211,153,0.12)]'
            }`}
          >
            {t(`documents.categoryLabels.${key}`) || cat.label} ({categoryCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Search & Sort Toolbar - Light Mode Base with Dark Mode Override - UNIFIED DARK CONTROLS */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px_105px] gap-3 mb-[18px]">
        <div className="relative">
          <Search
            size={17}
            className="absolute left-[14px] top-1/2 -translate-y-1/2 pointer-events-none text-stone-400 dark:text-[#5ea882]"
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('documents.searchDocuments')}
            className="w-full h-[42px] rounded-[14px] pl-10 pr-[14px] outline-none bg-stone-50 border border-stone-200 text-stone-900 placeholder:text-stone-400 dark:bg-[rgba(5,24,17,0.80)] dark:border-[rgba(52,211,153,0.10)] dark:text-[#e8fef1] dark:placeholder:text-[#5ea882] focus:dark:border-[rgba(52,211,153,0.20)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-[42px] rounded-[14px] px-[14px] outline-none cursor-pointer bg-stone-50 border border-stone-200 text-stone-900 dark:bg-[rgba(5,24,17,0.80)] dark:border-[rgba(52,211,153,0.10)] dark:text-[#c8e8d6] appearance-none"
          style={{
            backgroundImage: 'linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)',
            backgroundPosition: 'calc(100% - 18px) 17px, calc(100% - 12px) 17px',
            backgroundSize: '6px 6px, 6px 6px',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <option value="all">{t('documents.allStatuses')}</option>
          {Object.entries(DOCUMENT_STATUS).map(([key]) => (
            <option key={key} value={key}>
              {getDocumentStatusLabel(key, t)}
            </option>
          ))}
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="h-[42px] rounded-[14px] px-[14px] outline-none cursor-pointer bg-stone-50 border border-stone-200 text-stone-900 dark:bg-[rgba(5,24,17,0.80)] dark:border-[rgba(52,211,153,0.10)] dark:text-[#c8e8d6] appearance-none"
          style={{
            backgroundImage: 'linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)',
            backgroundPosition: 'calc(100% - 18px) 17px, calc(100% - 12px) 17px',
            backgroundSize: '6px 6px, 6px 6px',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <option value="newest">{t('common.newest')}</option>
          <option value="oldest">{t('common.oldest')}</option>
        </select>
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {filteredDocuments.length === 0 ? (
          /* Empty State - Light Mode Base with Dark Mode Override - UNIFIED DARK PREMIUM CARD */
          <div className="relative overflow-hidden min-h-[206px] rounded-[20px] flex items-center justify-center text-center p-6 bg-white border border-stone-200 dark:bg-[linear-gradient(180deg,rgba(6,32,22,0.96)_0%,rgba(4,22,15,0.98)_100%)] dark:border-[rgba(52,211,153,0.10)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
            {/* Dark mode subtle glow */}
            <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 bg-[radial-gradient(circle_at_50%_0%,rgba(52,211,153,0.04),transparent_30%)]" />
            <div className="max-w-[420px] relative z-10">
              <div className="w-[74px] h-[74px] mx-auto mb-4 rounded-[20px] flex items-center justify-center bg-stone-100 border border-stone-200 text-stone-400 dark:bg-[rgba(52,211,153,0.04)] dark:border-[rgba(52,211,153,0.08)] dark:text-[#7fb89a]">
                <FolderOpen size={34} />
              </div>
              <h3 className="text-[24px] font-extrabold mb-2 tracking-[-0.02em] leading-[1.15] text-stone-900 dark:text-[#e8fef1]">
                {searchQuery ? t('common.noResults') : t('documents.noDocuments')}
              </h3>
              <p className="text-[15px] leading-[1.6] mb-[18px] text-stone-500 dark:text-[#7fb89a]">
                {searchQuery
                  ? t('common.noResults')
                  : t('documents.uploadYourFirst')}
              </p>
              {!searchQuery && (
                <div className="flex items-center justify-center gap-2.5 flex-wrap">
                  <Button 
                    disabled
                    variant="outline"
                    className="h-[42px] rounded-[14px] px-4 font-bold border-stone-300 text-stone-400 cursor-not-allowed opacity-50 dark:border-[rgba(52,211,153,0.08)] dark:text-[#7a9f8a]"
                  >
                    <Smartphone size={16} className="mr-2" />
                    {t('documents.scanWithPhone')}
                    <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-semibold">{t('documents.soonBadge')}</span>
                  </Button>
                  <Button
                    onClick={() => openUploadModal()}
                    className="h-[42px] rounded-[14px] px-4 font-bold bg-gradient-to-b from-[#20c669] to-[#12aa5b] text-white border-0 shadow-[0_10px_20px_rgba(18,170,91,0.22)]"
                  >
                    <Upload size={16} className="mr-2" />
                    {t('common.upload')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.document_id}
              document={doc}
              onView={setPreviewDocument}
              onEdit={setEditDocument}
              onDelete={handleDelete}
              onApprove={handleApprove}
              onRevokeApproval={handleRevokeApproval}
            />
          ))
        )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadCategory('');
        }}
        onUpload={handleUpload}
        preselectedCategory={uploadCategory}
        t={t}
      />

      {/* QR Scan Modal */}
      <QRScanModal
        isOpen={qrScanModalOpen}
        onClose={() => setQrScanModalOpen(false)}
        onSessionCreated={() => fetchDocuments({ force: true })}
        t={t}
      />

      {/* Preview Modal */}
      <PreviewModal
        document={previewDocument}
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
      />

      {/* Image Editor Modal */}
      <ImageEditorModal
        isOpen={!!editDocument}
        onClose={() => setEditDocument(null)}
        document={editDocument}
        onSave={fetchDocuments}
      />
    </div>
  );
};

export default MyDocumentsPage;
